import type { IAPComponentTypes } from '@play-co/components';
import type { ProductId } from '../iap.ruleset';
import { tuple } from 'src/replicant/utils/types';

type CatalogItem = IAPComponentTypes.Options.VirtualItem<
  (typeof catalogLanguages)[number],
  (typeof catalogProviders)[number]
>;

/**
 * Catalog settings
 */
export const catalogVersion = '2023-07-24'; // If you change the catalog, update this version
export const catalogLanguages = tuple('ja', 'en');
export const catalogProviders = tuple('komoju', 'atone', 'stripe', 'line');

/**
 * BD IAP Catalog
 * @see LINE Price table: https://docs.google.com/spreadsheets/d/1uU_BF-m23JnvljeLKRvo5KF_yGVRtQmijYrXMYxziYs/edit?gid=1764586209#gid=1764586209
 */
export const catalog: Record<ProductId, CatalogItem> = {
  /** --------------------------------------------------------------
   * Gems only products
   * ---------------------------------------------------------------
   */
  buruburu_gems_100: {
    price: [100, 'JPY'],
    status: 'enabled',
    title: { ja: '100 ジェム', en: '100 Gems' },
    description: { ja: '100 ジェム', en: '100 Gems' },
    stripe: {
      livePriceId: 'price_1MgPzcCva6KSREGvZvVOzScF',
      testPriceId: 'price_1MgSNyCva6KSREGvmHWTWXp8',
    },
    line: {
      basePrice: [100, 'JPY'],
      productId: 'iap_ln_002',
    },
  },
  buruburu_gems_200: {
    price: [300, 'JPY'],
    status: 'enabled',
    title: { ja: '200 ジェム', en: '200 Gems' },
    description: { ja: '200 ジェム', en: '200 Gems' },
    stripe: {
      livePriceId: 'price_1NdqPXCva6KSREGv8evP2Zpp',
      testPriceId: 'price_1NcRnNCva6KSREGvI7zED1av',
    },
    line: {
      basePrice: [300, 'JPY'],
      productId: 'iap_ln_006',
    },
  },
  buruburu_gems_350: {
    price: [400, 'JPY'],
    status: 'enabled',
    title: { ja: '350 ジェム', en: '350 Gems' },
    description: { ja: '350 ジェム', en: '350 Gems' },
    stripe: {
      livePriceId: 'price_1MgQ00Cva6KSREGvRVh3TGGO',
      testPriceId: 'price_1MgSOICva6KSREGvt4t73opw',
    },
    line: {
      basePrice: [400, 'JPY'],
      productId: 'iap_ln_008',
    },
  },
  buruburu_gems_1000: {
    price: [1200, 'JPY'],
    status: 'enabled',
    title: { ja: '1000 ジェム', en: '1000 Gems' },
    description: { ja: '1000 ジェム', en: '1000 Gems' },
    stripe: {
      livePriceId: 'price_1MgQ0MCva6KSREGvHypQYHLp',
      testPriceId: 'price_1MgSObCva6KSREGvTwS2Bc9w',
    },
    line: {
      basePrice: [1200, 'JPY'],
      productId: 'iap_ln_022',
    },
  },
  buruburu_gems_1000_1200: {
    price: [1200, 'JPY'],
    status: 'enabled',
    title: { ja: '800 ジェム', en: '800 Gems' },
    description: { ja: '800 ジェム', en: '800 Gems' },
    stripe: {
      livePriceId: 'price_1NdqPxCva6KSREGvTxJ1u05W',
      testPriceId: 'price_1NcRqnCva6KSREGvp4EVn1Wc',
    },
    line: {
      basePrice: [1200, 'JPY'],
      productId: 'iap_ln_022',
    },
  },
  buruburu_gems_2000: {
    price: [2000, 'JPY'],
    status: 'enabled',
    title: { ja: '2000 ジェム', en: '2000 Gems' },
    description: { ja: '2000 ジェム', en: '2000 Gems' },
    stripe: {
      livePriceId: 'price_1NdqQhCva6KSREGvYAUuM5Qn',
      testPriceId: 'price_1NcRsMCva6KSREGvl488eUEq',
    },
    line: {
      basePrice: [2000, 'JPY'],
      productId: 'iap_ln_030',
    },
  },
  buruburu_gems_3200: {
    price: [3500, 'JPY'],
    status: 'enabled',
    title: { ja: '3200 ジェム', en: '3200 Gems' },
    description: { ja: '3200 ジェム', en: '3200 Gems' },
    stripe: {
      livePriceId: 'price_1MgQ0gCva6KSREGvu4cMBfqs',
      testPriceId: 'price_1MgSOwCva6KSREGvgiDNEOt7',
    },
    line: {
      basePrice: [4000, 'JPY'],
      productId: 'iap_ln_050',
    },
  },
  buruburu_gems_5500: {
    price: [5000, 'JPY'],
    status: 'enabled',
    title: { ja: '5500 ジェム', en: '5500 Gems' },
    description: { ja: '5500 ジェム', en: '5500 Gems' },
    stripe: {
      livePriceId: 'price_1NdqQqCva6KSREGvTJw320mf',
      testPriceId: 'price_1NcRtgCva6KSREGvJUOXl3XV',
    },
    line: {
      basePrice: [5000, 'JPY'],
      productId: 'iap_ln_060',
    },
  },
  buruburu_gems_6000: {
    price: [6000, 'JPY'],
    status: 'enabled',
    title: { ja: '6000 ジェム', en: '6000 Gems' },
    description: { ja: '6000 ジェム', en: '6000 Gems' },
    stripe: {
      livePriceId: 'price_1MgQ1JCva6KSREGvy6FanvP1',
      testPriceId: 'price_1MgSPLCva6KSREGvHLUSzRAn',
    },
    line: {
      basePrice: [6000, 'JPY'],
      productId: 'iap_ln_070',
    },
  },
  buruburu_gems_10000: {
    price: [8000, 'JPY'],
    status: 'enabled',
    title: { ja: '10000 ジェム', en: '10000 Gems' },
    description: { ja: '10000 ジェム', en: '10000 Gems' },
    stripe: {
      livePriceId: 'price_1NdqQyCva6KSREGvg73hShpA',
      testPriceId: 'price_1NcRuXCva6KSREGvnBCkZ0TV',
    },
    line: {
      basePrice: [8000, 'JPY'],
      productId: 'iap_ln_090',
    },
  },
  buruburu_gems_12000: {
    price: [11000, 'JPY'],
    status: 'enabled',
    title: { ja: '12000 ジェム', en: '12000 Gems' },
    description: { ja: '12000 ジェム', en: '12000 Gems' },
    stripe: {
      livePriceId: 'price_1MgQ1mCva6KSREGvaRJy66G1',
      testPriceId: 'price_1MgSPxCva6KSREGvbk52rDUy',
    },
    line: {
      basePrice: [12000, 'JPY'],
      productId: 'iap_ln_130',
    },
  },
  buruburu_gems_18000: {
    price: [15000, 'JPY'],
    status: 'enabled',
    title: { ja: '18000 ジェム', en: '18000 Gems' },
    description: { ja: '18000 ジェム', en: '18000 Gems' },
    stripe: {
      livePriceId: 'price_1NfEulCva6KSREGvSNj4z2Tw',
      testPriceId: 'price_1MgSQCCva6KSREGv7ZQFJlTl',
    },
    line: {
      basePrice: [15000, 'JPY'],
      productId: 'iap_ln_160',
    },
  },
  buruburu_gems_20000: {
    price: [14000, 'JPY'],
    status: 'enabled',
    title: { ja: '20000 ジェム', en: '20000 Gems' },
    description: { ja: '20000 ジェム', en: '20000 Gems' },
    stripe: {
      livePriceId: 'price_1NdqR7Cva6KSREGvkWPuGj2U',
      testPriceId: 'price_1NcRwGCva6KSREGvTkoMAeIw',
    },
    line: {
      basePrice: [14000, 'JPY'],
      productId: 'iap_ln_150',
    },
  },

  /** --------------------------------------------------------------
   * Starter pack products
   *  --------------------------------------------------------------
   */
  buruburu_pet_starter_pack: {
    price: [10, 'JPY'],
    status: 'enabled',
    title: { ja: '初心者ペットパック', en: 'Pet Starter Pack' },
    description: { ja: '初心者ペットパック', en: 'Pet Starter Pack' },
    stripe: {
      // Price is too low for Stripe so it won't work but we decided to keep it to have some behavior like previous implementation
      // Stripe min is 50 JPY
      livePriceId: 'price_1PucvTCva6KSREGvb91ZDhZj',
      testPriceId: 'price_1PucyOCva6KSREGvguwDAkTm',
    },
    line: {
      basePrice: [100, 'JPY'],
      productId: 'iap_ln_002',
    },
  },
  buruburu_pet_starter_pack_2: {
    price: [10, 'JPY'],
    status: 'enabled',
    title: { ja: '初回限定パック', en: 'One-Time Pack' },
    description: { ja: '初回限定パック', en: 'One-Time Pack' },
    stripe: {
      // Price is too low for Stripe so it won't work but we decided to keep it to have some behavior like previous implementation
      // Stripe min is 50 JPY
      livePriceId: 'price_1Q825NCva6KSREGvwJAEKn1I',
      testPriceId: 'price_1Q80B1Cva6KSREGvifn9jt8d',
    },
    line: {
      basePrice: [100, 'JPY'],
      productId: 'iap_ln_002',
    },
  },
  buruburu_pet_starter_pack_3: {
    price: [10, 'JPY'],
    status: 'enabled',
    title: { ja: '初回限定パック', en: 'One-Time Pack' },
    description: { ja: '初回限定パック', en: 'One-Time Pack' },
    stripe: {
      // Price is too low for Stripe so it won't work but we decided to keep it to have some behavior like previous implementation
      // Stripe min is 50 JPY
      livePriceId: 'price_1QQAykCva6KSREGvKMrRK0jD',
      testPriceId: 'price_1QQAwwCva6KSREGvVFeugRT7',
    },
    line: {
      basePrice: [100, 'JPY'],
      productId: 'iap_ln_002',
    },
  },
  buruburu_starter_pack: {
    price: [150, 'JPY'],
    status: 'enabled',
    title: { ja: '初心者ペットパック', en: 'Pet Starter Pack' },
    description: { ja: '初心者ペットパック', en: 'Pet Starter Pack' },
    stripe: {
      livePriceId: 'price_1NdqRECva6KSREGvXcbjmqlL',
      testPriceId: 'price_1NcRxgCva6KSREGvQ086bK94',
    },
    line: {
      basePrice: [150, 'JPY'],
      productId: 'iap_ln_003',
    },
  },

  /** --------------------------------------------------------------
   * Permanent pack products
   * ---------------------------------------------------------------
   */
  buruburu_mini_pack: {
    price: [1000, 'JPY'],
    status: 'enabled',
    title: { ja: 'ミニペットパック', en: 'Mini Pack' },
    description: { ja: 'ミニペットパック', en: 'Mini Pack' },
    stripe: {
      livePriceId: 'price_1NdqRMCva6KSREGvWlxIrZwx',
      testPriceId: 'price_1NcRymCva6KSREGvALEQb0c6',
    },
    line: {
      basePrice: [1000, 'JPY'],
      productId: 'iap_ln_020',
    },
  },
  buruburu_small_pack: {
    price: [1500, 'JPY'],
    status: 'enabled',
    title: { ja: 'スモールペットパック', en: 'Small Pet Pack' },
    description: { ja: 'スモールペットパック', en: 'Small Pet Pack' },
    stripe: {
      livePriceId: 'price_1NdqRRCva6KSREGvs13evKvH',
      testPriceId: 'price_1NcRzVCva6KSREGvCUXPRaQD',
    },
    line: {
      basePrice: [1500, 'JPY'],
      productId: 'iap_ln_025',
    },
  },
  buruburu_medium_pack: {
    price: [3000, 'JPY'],
    status: 'enabled',
    title: { ja: 'ミディアムペットパック', en: 'Medium Pet Pack' },
    description: { ja: 'ミディアムペットパック', en: 'Medium Pet Pack' },
    stripe: {
      livePriceId: 'price_1NdqRXCva6KSREGvThadNbBl',
      testPriceId: 'price_1NcS0TCva6KSREGvviOLyj2p',
    },
    line: {
      basePrice: [3000, 'JPY'],
      productId: 'iap_ln_040',
    },
  },
  buruburu_large_pack: {
    price: [6000, 'JPY'],
    status: 'enabled',
    title: { ja: 'ラージペットパック', en: 'Large Pet Pack' },
    description: { ja: 'ラージペットパック', en: 'Large Pet Pack' },
    stripe: {
      livePriceId: 'price_1NdqRcCva6KSREGvkd0qgqmq',
      testPriceId: 'price_1NcS1gCva6KSREGvzm5ytbaV',
    },
    line: {
      basePrice: [6000, 'JPY'],
      productId: 'iap_ln_070',
    },
  },
  buruburu_grande_pack: {
    price: [12000, 'JPY'],
    status: 'enabled',
    title: { ja: 'グランデペットパック', en: 'Grande Pet Pack' },
    description: { ja: 'グランデペットパック', en: 'Grande Pet Pack' },
    stripe: {
      livePriceId: 'price_1NdqRiCva6KSREGv5wseHl1y',
      testPriceId: 'price_1NcS2RCva6KSREGv7Gb57tFj',
    },
    line: {
      basePrice: [12000, 'JPY'],
      productId: 'iap_ln_130',
    },
  },
  buruburu_jumbo_pack: {
    price: [14500, 'JPY'],
    status: 'enabled',
    title: { ja: 'ジャンボペットパック', en: 'Jumbo Pet Pack' },
    description: { ja: 'ジャンボペットパック', en: 'Jumbo Pet Pack' },
    stripe: {
      livePriceId: 'price_1NdqRoCva6KSREGvpR9GdxTJ',
      testPriceId: 'price_1NcS30Cva6KSREGvPP5pr35g',
    },
    line: {
      basePrice: [14500, 'JPY'],
      productId: 'iap_ln_155',
    },
  },

  /**
   * Booster sale products
   */
  buruburu_booster_sale: {
    price: [1000, 'JPY'],
    status: 'enabled',
    title: { ja: 'ブースターセール', en: 'Booster sale' },
    description: { ja: 'ブースターセール', en: 'Booster sale' },
    stripe: {
      livePriceId: 'price_1NdsADCva6KSREGvIc4DIXue',
      testPriceId: 'price_1Nds9uCva6KSREGvhTO2GU8P',
    },
    line: {
      basePrice: [1000, 'JPY'],
      productId: 'iap_ln_020',
    },
  },

  /**
   * Endless sale products
   */
  buruburu_sale_rm_04p_2: {
    price: [400, 'JPY'],
    status: 'enabled',
    title: { ja: '連続セールパック', en: 'Endless sale pack' },
    description: { ja: '連続セールパック', en: 'Endless sale pack' },
    stripe: {
      livePriceId: 'price_1ODM68Cva6KSREGvtz7vvqdX',
      testPriceId: 'price_1ODLqbCva6KSREGvVp5q3IXO',
    },
    line: {
      basePrice: [400, 'JPY'],
      productId: 'iap_ln_008',
    },
  },
  buruburu_sale_rm_10p: {
    price: [700, 'JPY'],
    status: 'enabled',
    title: { ja: '連続セールパック', en: 'Endless sale pack' },
    description: { ja: '連続セールパック', en: 'Endless sale pack' },
    stripe: {
      livePriceId: 'price_1ODM6dCva6KSREGvNX3IrSCW',
      testPriceId: 'price_1ODLsyCva6KSREGvH7o0Mj8y',
    },
    line: {
      basePrice: [700, 'JPY'],
      productId: 'iap_ln_014',
    },
  },
  buruburu_sale_rm_16p: {
    price: [1200, 'JPY'],
    status: 'enabled',
    title: { ja: '連続セールパック', en: 'Endless sale pack' },
    description: { ja: '連続セールパック', en: 'Endless sale pack' },
    stripe: {
      livePriceId: 'price_1ODM78Cva6KSREGvgJzjrTHP',
      testPriceId: 'price_1ODLttCva6KSREGv7yERVXNE',
    },
    line: {
      basePrice: [1200, 'JPY'],
      productId: 'iap_ln_022',
    },
  },
  buruburu_sale_rm_22p: {
    price: [2200, 'JPY'],
    status: 'enabled',
    title: { ja: '連続セールパック', en: 'Endless sale pack' },
    description: { ja: '連続セールパック', en: 'Endless sale pack' },
    stripe: {
      livePriceId: 'price_1ODM7aCva6KSREGvMbHDRNg0',
      testPriceId: 'price_1ODLuaCva6KSREGvRpM61vx3',
    },
    line: {
      basePrice: [2200, 'JPY'],
      productId: 'iap_ln_032',
    },
  },
  buruburu_sale_rm_28p: {
    price: [3000, 'JPY'],
    status: 'enabled',
    title: { ja: '連続セールパック', en: 'Endless sale pack' },
    description: { ja: '連続セールパック', en: 'Endless sale pack' },
    stripe: {
      livePriceId: 'price_1ODM88Cva6KSREGvc51HDlzt',
      testPriceId: 'price_1ODLvACva6KSREGv0r0pFVr0',
    },
    line: {
      basePrice: [3000, 'JPY'],
      productId: 'iap_ln_040',
    },
  },
  buruburu_sale_rm_34p: {
    price: [4500, 'JPY'],
    status: 'enabled',
    title: { ja: '連続セールパック', en: 'Endless sale pack' },
    description: { ja: '連続セールパック', en: 'Endless sale pack' },
    stripe: {
      livePriceId: 'price_1ODM8bCva6KSREGvOcKJE6Wf',
      testPriceId: 'price_1ODLvhCva6KSREGvFrbZfgLe',
    },
    line: {
      basePrice: [4500, 'JPY'],
      productId: 'iap_ln_055',
    },
  },
  buruburu_sale_rm_40p: {
    price: [7000, 'JPY'],
    status: 'enabled',
    title: { ja: '連続セールパック', en: 'Endless sale pack' },
    description: { ja: '連続セールパック', en: 'Endless sale pack' },
    stripe: {
      livePriceId: 'price_1ODM95Cva6KSREGvmSckURt1',
      testPriceId: 'price_1ODLwCCva6KSREGvzZewhcZb',
    },
    line: {
      basePrice: [7000, 'JPY'],
      productId: 'iap_ln_080',
    },
  },
  buruburu_sale_rm_46p: {
    price: [15000, 'JPY'],
    status: 'enabled',
    title: { ja: '連続セールパック', en: 'Endless sale pack' },
    description: { ja: '連続セールパック', en: 'Endless sale pack' },
    stripe: {
      livePriceId: 'price_1ODM9bCva6KSREGveQTUmeGa',
      testPriceId: 'price_1ODLwuCva6KSREGvt1zaBNlK',
    },
    line: {
      basePrice: [15000, 'JPY'],
      productId: 'iap_ln_160',
    },
  },
  buruburu_sale_cm_04p: {
    price: [580, 'JPY'],
    status: 'enabled',
    title: { ja: '連続セールパック', en: 'Endless sale pack' },
    description: { ja: '連続セールパック', en: 'Endless sale pack' },
    stripe: {
      livePriceId: 'price_1ODMA9Cva6KSREGvkbGRVqTU',
      testPriceId: 'price_1ODLxQCva6KSREGvFIb2rZqn',
    },
    line: {
      basePrice: [700, 'JPY'],
      productId: 'iap_ln_014',
    },
  },
  buruburu_sale_cm_10p: {
    price: [1000, 'JPY'],
    status: 'enabled',
    title: { ja: '連続セールパック', en: 'Endless sale pack' },
    description: { ja: '連続セールパック', en: 'Endless sale pack' },
    stripe: {
      livePriceId: 'price_1ODMAdCva6KSREGvew9gC0Qc',
      testPriceId: 'price_1ODLxuCva6KSREGvJKogSIWx',
    },
    line: {
      basePrice: [1000, 'JPY'],
      productId: 'iap_ln_020',
    },
  },
  buruburu_sale_cm_16p: {
    price: [1450, 'JPY'],
    status: 'enabled',
    title: { ja: '連続セールパック', en: 'Endless sale pack' },
    description: { ja: '連続セールパック', en: 'Endless sale pack' },
    stripe: {
      livePriceId: 'price_1ODMB5Cva6KSREGvFdUv04Mf',
      testPriceId: 'price_1ODLyRCva6KSREGvw8PN4Sy4',
    },
    line: {
      basePrice: [1500, 'JPY'],
      productId: 'iap_ln_025',
    },
  },
  buruburu_sale_cm_22p: {
    price: [2200, 'JPY'],
    status: 'enabled',
    title: { ja: '連続セールパック', en: 'Endless sale pack' },
    description: { ja: '連続セールパック', en: 'Endless sale pack' },
    stripe: {
      livePriceId: 'price_1ODMBkCva6KSREGvsMSbPEAy',
      testPriceId: 'price_1ODLyvCva6KSREGvRWdupo1y',
    },
    line: {
      basePrice: [2200, 'JPY'],
      productId: 'iap_ln_032',
    },
  },
  buruburu_sale_cm_28p: {
    price: [2900, 'JPY'],
    status: 'enabled',
    title: { ja: '連続セールパック', en: 'Endless sale pack' },
    description: { ja: '連続セールパック', en: 'Endless sale pack' },
    stripe: {
      livePriceId: 'price_1ODMCGCva6KSREGvOd0tCXal',
      testPriceId: 'price_1ODLzRCva6KSREGvsgxsoZ7v',
    },
    line: {
      basePrice: [3000, 'JPY'],
      productId: 'iap_ln_040',
    },
  },
  buruburu_sale_cm_34p: {
    price: [3600, 'JPY'],
    status: 'enabled',
    title: { ja: '連続セールパック', en: 'Endless sale pack' },
    description: { ja: '連続セールパック', en: 'Endless sale pack' },
    stripe: {
      livePriceId: 'price_1ODMCpCva6KSREGvQg0s3sWq',
      testPriceId: 'price_1ODLzxCva6KSREGvqj6ICgFD',
    },
    line: {
      basePrice: [4000, 'JPY'],
      productId: 'iap_ln_050',
    },
  },
  buruburu_sale_cm_40p: {
    price: [4300, 'JPY'],
    status: 'enabled',
    title: { ja: '連続セールパック', en: 'Endless sale pack' },
    description: { ja: '連続セールパック', en: 'Endless sale pack' },
    stripe: {
      livePriceId: 'price_1ODME5Cva6KSREGv1dTemWf7',
      testPriceId: 'price_1ODM0fCva6KSREGvLA965WTK',
    },
    line: {
      basePrice: [4000, 'JPY'],
      productId: 'iap_ln_050',
    },
  },
  buruburu_sale_cm_46p: {
    price: [5780, 'JPY'],
    status: 'enabled',
    title: { ja: '連続セールパック', en: 'Endless sale pack' },
    description: { ja: '連続セールパック', en: 'Endless sale pack' },
    stripe: {
      livePriceId: 'price_1ODMEiCva6KSREGvhz7Zas7p',
      testPriceId: 'price_1ODM1ECva6KSREGvHhsjjKTo',
    },
    line: {
      basePrice: [6000, 'JPY'],
      productId: 'iap_ln_070',
    },
  },
  buruburu_sale_cm_52p: {
    price: [7200, 'JPY'],
    status: 'enabled',
    title: { ja: '連続セールパック', en: 'Endless sale pack' },
    description: { ja: '連続セールパック', en: 'Endless sale pack' },
    stripe: {
      livePriceId: 'price_1ODMFDCva6KSREGvvt1Djoyx',
      testPriceId: 'price_1ODM2CCva6KSREGvr3DLA8hC',
    },
    line: {
      basePrice: [8000, 'JPY'],
      productId: 'iap_ln_090',
    },
  },
  buruburu_sale_cm_58p: {
    price: [14400, 'JPY'],
    status: 'enabled',
    title: { ja: '連続セールパック', en: 'Endless sale pack' },
    description: { ja: '連続セールパック', en: 'Endless sale pack' },
    stripe: {
      livePriceId: 'price_1ODMFqCva6KSREGvODuLWs6K',
      testPriceId: 'price_1ODM2oCva6KSREGvnquS5mgb',
    },
    line: {
      basePrice: [14500, 'JPY'],
      productId: 'iap_ln_155',
    },
  },

  /**
   * Disabled products
   */
  buruburu_buru_pass: {
    price: [1400, 'JPY'],
    status: 'disabled',
    title: { ja: 'ブルーパス', en: 'Buru Pass' },
    description: { ja: 'ブルーパス', en: 'Buru Pass' },
    stripe: {
      livePriceId: 'price_1NlF2vCva6KSREGvXv0MbUfP',
      testPriceId: 'price_1NlF2ZCva6KSREGvglNHaHp4',
    },
  },
  buruburu_flashsale_continue: {
    price: [300, 'JPY'],
    status: 'disabled',
    title: { ja: 'Flash Sale', en: 'Flash Sale' },
    description: { ja: 'Flash Sale', en: 'Flash Sale' },
    stripe: {
      livePriceId: 'price_1OfMxaCva6KSREGvSJbM4EhT',
      testPriceId: 'price_1OgQ0vCva6KSREGvwq84ObIb',
    },
  },
  buruburu_conversionsale_foryou: {
    price: [100, 'JPY'],
    status: 'disabled',
    title: { ja: 'Sale For You', en: 'Sale For You' },
    description: { ja: 'Sale For You', en: 'Sale For You' },
    stripe: {
      livePriceId: 'price_1Oh83pCva6KSREGvuPqUIyhV',
      testPriceId: 'price_1Oh84eCva6KSREGvcFvAoeIC',
    },
  },

  /** --------------------------------------------------------------
   * Piggy Bank products
   * ---------------------------------------------------------------
   */
  buruburu_piggy_bank_200: {
    price: [200, 'JPY'],
    status: 'disabled',
    title: { ja: '貯金箱', en: 'Piggy Bank' },
    description: { ja: '貯金箱 (¥200)', en: 'Piggy Bank (¥200)' },
    stripe: {
      livePriceId: 'price_TBD_piggy_bank_200',
      testPriceId: 'price_TBD_piggy_bank_200',
    },
    line: {
      basePrice: [200, 'JPY'],
      productId: 'iap_ln_019',
    },
  },
  buruburu_piggy_bank_500: {
    price: [500, 'JPY'],
    status: 'disabled',
    title: { ja: '貯金箱', en: 'Piggy Bank' },
    description: { ja: '貯金箱 (¥500)', en: 'Piggy Bank (¥500)' },
    stripe: {
      livePriceId: 'price_TBD_piggy_bank_500',
      testPriceId: 'price_TBD_piggy_bank_500',
    },
    line: {
      basePrice: [500, 'JPY'],
      productId: 'iap_ln_059',
    },
  },
};
